import { AlertCircle, ChevronRight, Loader, PackageX, RotateCcw, Search, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { listReturns, getReturnDetail, issueRefund, getImageBySku } from "../services/returnService";

const money = (amount, currency = "USD") => {
    if (amount === undefined || amount === null) return "—";
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
    } catch {
        return `$${Number(amount).toFixed(2)}`;
    }
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const customerFullName = (name) =>
    [name?.firstName, name?.lastName].filter(Boolean).join(" ") || "—";

// Reason codes are shouty SCREAMING_SNAKE from Walmart — make them
// readable without a lookup table.
const humanizeCode = (code) =>
    !code ? "" : code.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const STATUS_STYLES = {
    INITIATED: "bg-orange-50 text-orange-600",
    DELIVERED: "bg-blue-50 text-blue-600",
    COMPLETED: "bg-green-50 text-green-600",
};
const StatusBadge = ({ status }) => (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[status] || "bg-gray-100 text-gray-500"}`}>
        {status || "Unknown"}
    </span>
);

const REFUND_STATUS_STYLES = {
    REFUNDED: "bg-green-50 text-green-600",
    REFUND_INITIATED: "bg-blue-50 text-blue-600",
    PENDING: "bg-gray-100 text-gray-500",
};
const RefundStatusBadge = ({ status }) => {
    if (!status) return null;
    return (
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${REFUND_STATUS_STYLES[status] || "bg-gray-100 text-gray-500"}`}>
            {humanizeCode(status)}
        </span>
    );
};

const ProductImage = ({ src, fallback = "📦" }) => {
    const [errored, setErrored] = useState(false);
    if (!src || errored) return <span className="text-2xl">{fallback}</span>;
    return <img src={src} alt="" onError={() => setErrored(true)} className="w-11 h-11 object-contain rounded flex-shrink-0" />;
};

const ReturnCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3 animate-pulse">
        <div className="w-11 h-11 rounded bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
            <div className="h-3 bg-gray-200 rounded w-1/3" />
            <div className="h-2.5 bg-gray-100 rounded w-2/3" />
            <div className="h-2.5 bg-gray-100 rounded w-1/4" />
        </div>
        <div className="w-16 h-5 bg-gray-200 rounded-full flex-shrink-0" />
    </div>
);

// Refund line row — one per returnOrderLine, with a checkbox and an
// editable "quantity to refund" that defaults to whatever's left
// unrefunded and clamps to that max.
const RefundLineRow = ({ line, selected, qty, onToggle, onQtyChange, imageUrl }) => {
    const item = line.item || {};
    const requestedQty = line.quantity?.measurementValue ?? 0;
    const refundedQty = line.refundedQty || 0;
    const remaining = Math.max(requestedQty - refundedQty, 0);
    const alreadyDone = remaining <= 0;

    return (
        <tr className="border-b border-gray-50 align-top">
            <td className="py-2 pr-2">
                <input type="checkbox" checked={selected} disabled={alreadyDone}
                    onChange={() => onToggle(line.returnOrderLineNumber)}
                    className="accent-teal-600 disabled:opacity-30" />
            </td>
            <td className="py-2 pr-3">
                <div className="flex items-start gap-2">
                    <ProductImage src={imageUrl} />
                    <div>
                        <p className="text-gray-700 font-medium">{item.productName || item.title || `Line ${line.returnOrderLineNumber}`}</p>
                        {item.sku && <p className="text-gray-400 font-mono text-[11px]">{item.sku}</p>}
                        <p className="text-gray-400 text-[11px] mt-0.5">{humanizeCode(line.returnReason)}</p>
                    </div>
                </div>
            </td>
            <td className="py-2 pr-3 text-gray-600">{requestedQty}</td>
            <td className="py-2 pr-3 text-gray-600">{refundedQty}</td>
            <td className="py-2 pr-3">
                <RefundStatusBadge status={line.currentRefundStatus} />
            </td>
            <td className="py-2">
                {alreadyDone ? (
                    <span className="text-[11px] text-gray-300">Fully refunded</span>
                ) : (
                    <input type="number" min={1} max={remaining} value={qty}
                        disabled={!selected}
                        onChange={(e) => onQtyChange(line.returnOrderLineNumber, Number(e.target.value))}
                        className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-xs disabled:opacity-40 disabled:bg-gray-50 focus:outline-none focus:border-teal-500" />
                )}
            </td>
        </tr>
    );
};

const ReturnDetailModal = ({ returnOrderId, onClose, onRefunded }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [qtyByLine, setQtyByLine] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [refundResult, setRefundResult] = useState(null);
    const [modalImageBySku, setModalImageBySku] = useState({});

    const load = () => getReturnDetail(returnOrderId)
        .then((d) => {
            setData(d.return_order);
            // Pre-fill each refundable line's quantity input with its
            // full remaining (unrefunded) amount.
            const initial = {};
            for (const line of d.return_order.returnOrderLines || []) {
                const remaining = (line.quantity?.measurementValue ?? 0) - (line.refundedQty || 0);
                if (remaining > 0) initial[line.returnOrderLineNumber] = remaining;
            }
            setQtyByLine(initial);
        })
        .catch((e) => setError(e.message || "Failed to load return"));
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [returnOrderId]);

    // Enrich every line's product image, one SKU at a time, same pattern
    // as the list view.
    useEffect(() => {
        const lines = data?.returnOrderLines || [];
        const skus = [...new Set(lines.map((l) => l.item?.sku).filter(Boolean))]
            .filter((sku) => modalImageBySku[sku] === undefined);
        if (skus.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const sku of skus) {
                if (cancelled) return;
                const result = await getImageBySku(sku).catch(() => null);
                if (cancelled) return;
                setModalImageBySku((prev) => ({ ...prev, [sku]: result?.image_url || null }));
                await new Promise((r) => setTimeout(r, 250));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.returnOrderLines?.length]);

    const toggleLine = (lineNumber) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(lineNumber) ? next.delete(lineNumber) : next.add(lineNumber);
        return next;
    });

    const handleRefund = async () => {
        if (selected.size === 0) return;
        setSubmitting(true);
        setError("");
        try {
            const refundLines = [...selected].map((lineNumber) => ({
                returnOrderLineNumber: lineNumber,
                quantity: { unitOfMeasure: "EA", measurementValue: qtyByLine[lineNumber] },
            }));
            const result = await issueRefund(returnOrderId, data.customerOrderId, refundLines);
            setRefundResult(result);
            setSelected(new Set());
            await load();
            onRefunded();
        } catch (e) {
            setError(e.message || "Failed to issue refund");
        } finally {
            setSubmitting(false);
        }
    };

    if (!data) {
        return (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 flex items-center gap-2 text-gray-400 text-sm">
                    <Loader size={16} className="animate-spin" /> Loading return…
                </div>
            </div>
        );
    }

    const lines = data.returnOrderLines || [];
    const refundableLines = lines.filter((l) => ((l.quantity?.measurementValue ?? 0) - (l.refundedQty || 0)) > 0);

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                    <div>
                        <p className="font-mono font-bold text-gray-800">{data.returnOrderId}</p>
                        <p className="text-xs text-gray-400">
                            Order {data.customerOrderId} · {customerFullName(data.customerName)} · {fmtDate(data.returnOrderDate)}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs text-red-700 flex items-start gap-2">
                            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
                        </div>
                    )}
                    {refundResult && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-xs text-green-700">
                            Refund submitted to Walmart for {refundResult.refundLines?.length || 0} line(s).
                        </div>
                    )}

                    <div className="grid grid-cols-3 gap-3 mb-5 text-xs">
                        <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-gray-400 mb-0.5">Return type</p>
                            <p className="font-bold text-gray-700">{humanizeCode(data.returnType)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-gray-400 mb-0.5">Refund mode</p>
                            <p className="font-bold text-gray-700">{humanizeCode(data.refundMode)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-gray-400 mb-0.5">Total refund amount</p>
                            <p className="font-bold text-gray-700">{money(data.totalRefundAmount?.currencyAmount, data.totalRefundAmount?.currencyUnit)}</p>
                        </div>
                    </div>

                    <table className="w-full text-xs mb-4">
                        <thead>
                            <tr className="text-gray-400 text-left border-b border-gray-100">
                                <th className="py-1.5 w-8"></th>
                                <th className="py-1.5">Item / reason</th>
                                <th>Qty</th>
                                <th>Refunded</th>
                                <th>Refund status</th>
                                <th>Refund qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((l) => (
                                <RefundLineRow key={l.returnOrderLineNumber} line={l}
                                    selected={selected.has(l.returnOrderLineNumber)}
                                    qty={qtyByLine[l.returnOrderLineNumber] || ""}
                                    onToggle={toggleLine}
                                    onQtyChange={(ln, v) => setQtyByLine((prev) => ({ ...prev, [ln]: v }))}
                                    imageUrl={modalImageBySku[l.item?.sku]} />
                            ))}
                        </tbody>
                    </table>

                    {refundableLines.length > 0 ? (
                        <button onClick={handleRefund} disabled={submitting || selected.size === 0}
                            className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition disabled:opacity-50 flex items-center justify-center gap-1.5">
                            {submitting ? "Issuing refund…" : <><RotateCcw size={14} /> Issue Refund ({selected.size} line{selected.size === 1 ? "" : "s"})</>}
                        </button>
                    ) : (
                        <p className="text-xs text-gray-400 text-center py-2">Every line on this return has already been fully refunded.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const ReturnsPage = () => {
    const [returns, setReturns] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");

    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [wfsFilter, setWfsFilter] = useState("");
    const [identifierType, setIdentifierType] = useState("returnOrderId"); // 'returnOrderId' | 'customerOrderId' | 'name'
    const [search, setSearch] = useState("");

    const [selectedRMA, setSelectedRMA] = useState(null);
    const sentinelRef = useRef(null);
    const [imageBySku, setImageBySku] = useState({});
    const imageAttemptedRef = useRef(new Set());

    const loadFirstPage = () => {
        setLoading(true);
        setError("");
        const filters = { status: statusFilter, returnType: typeFilter, isWfs: wfsFilter };
        // Walmart's API only supports filtering by returnOrderId (RMA #) or
        // customerOrderId — there is no server-side "search by name" at all.
        // "name" is handled client-side below instead (see visibleReturns).
        if (search.trim() && identifierType === "returnOrderId") filters.returnOrderId = search.trim();
        if (search.trim() && identifierType === "customerOrderId") filters.customerOrderId = search.trim();
        return listReturns(filters)
            .then((d) => {
                setReturns(d.returns || []);
                setNextCursor(d.next_cursor || null);
                setTotalCount(d.total_count || 0);
            })
            .catch((e) => setError(e.message || "Failed to load returns"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadFirstPage(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, typeFilter, wfsFilter]);

    // Switching identifier type while a value is already typed should
    // re-trigger the appropriate kind of search (server-side for RMA/Order
    // ID, or just re-filter client-side for name) rather than requiring a
    // second manual click of Search.
    useEffect(() => {
        if (!search.trim()) return;
        if (identifierType === "name") return; // handled client-side, no reload needed
        loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifierType]);

    const loadMore = useCallback(() => {
        if (!nextCursor || loadingMore) return;
        setLoadingMore(true);
        listReturns({ cursor: nextCursor })
            .then((d) => {
                setReturns((prev) => [...(prev || []), ...(d.returns || [])]);
                setNextCursor(d.next_cursor || null);
            })
            .catch((e) => setError(e.message || "Failed to load more returns"))
            .finally(() => setLoadingMore(false));
    }, [nextCursor, loadingMore]);

    // Infinite scroll: observe a 1px sentinel at the bottom of the list
    // and fetch the next page as it enters the viewport.
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadMore();
        }, { rootMargin: "200px" });
        observer.observe(el);
        return () => observer.disconnect();
    }, [loadMore]);

    // Enrich each visible return's first-line product image — one SKU at a
    // time, gently staggered, same pattern used for Orders/Reimbursements.
    // Attempted once per SKU per session regardless of outcome, so an
    // unfixable SKU (no catalog match at all) isn't retried every reload.
    useEffect(() => {
        if (!returns?.length) return;
        const skusNeeded = [...new Set(
            returns.map((r) => r.returnOrderLines?.[0]?.item?.sku).filter(Boolean)
        )].filter((sku) => !imageAttemptedRef.current.has(sku) && !imageBySku[sku]);
        if (skusNeeded.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const sku of skusNeeded) {
                if (cancelled) return;
                imageAttemptedRef.current.add(sku);
                const result = await getImageBySku(sku).catch(() => null);
                if (cancelled || !result?.image_url) continue;
                setImageBySku((prev) => ({ ...prev, [sku]: result.image_url }));
                await new Promise((r) => setTimeout(r, 250));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [returns?.length]);

    // Walmart's API has no name filter, so a "name" search only ever
    // narrows down whatever's already been loaded into `returns` — it does
    // NOT trigger a new server request. Keep loading more pages (scroll)
    // to widen the pool this filter searches over.
    const nameQuery = identifierType === "name" ? search.trim().toLowerCase() : "";
    const visibleReturns = nameQuery
        ? (returns || []).filter((r) => customerFullName(r.customerName).toLowerCase().includes(nameQuery))
        : returns;

    return (
        <div className="max-w-5xl mx-auto py-8 px-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-1">Returns</h1>
            <p className="text-gray-500 text-sm mb-6">Customer return orders (RMAs) and refunds, newest first.</p>

            <div className="bg-white rounded-xl shadow p-3 mb-4 flex flex-wrap items-center gap-2">
                <select value={identifierType} onChange={(e) => setIdentifierType(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white">
                    <option value="returnOrderId">RMA / Return Order ID</option>
                    <option value="customerOrderId">Customer Order ID</option>
                    <option value="name">Customer Name</option>
                </select>
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (identifierType === "name" ? null : loadFirstPage())}
                    placeholder={
                        identifierType === "returnOrderId" ? "Enter RMA / return order ID..."
                            : identifierType === "customerOrderId" ? "Enter customer order ID..."
                                : "Filter by customer name (loaded results only)..."
                    }
                    className="flex-1 min-w-[220px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">All statuses</option>
                    <option value="INITIATED">Initiated</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="COMPLETED">Completed</option>
                </select>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">All return types</option>
                    <option value="REFUND">Refund</option>
                    <option value="REPLACEMENT">Replacement</option>
                    <option value="PREORDER">Preorder</option>
                </select>
                <select value={wfsFilter} onChange={(e) => setWfsFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">WFS + Seller-fulfilled</option>
                    <option value="Y">WFS only</option>
                    <option value="N">Non-WFS only</option>
                </select>
                <button onClick={loadFirstPage} disabled={identifierType === "name"}
                    title={identifierType === "name" ? "Name filters as you type — no need to press Search" : undefined}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition disabled:opacity-40 flex items-center gap-1.5">
                    <Search size={14} /> Search
                </button>
            </div>

            {identifierType === "name" && (
                <p className="text-xs text-gray-400 mb-4 -mt-2">
                    Walmart doesn't support searching returns by name directly — this filters the {returns?.length || 0} return(s) already loaded below. Scroll down to load more before filtering, if the one you're after isn't showing up.
                </p>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs text-red-700 flex items-start gap-2">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
                </div>
            )}

            {returns === null && (
                <div className="space-y-2">
                    {Array.from({ length: 6 }).map((_, i) => <ReturnCardSkeleton key={i} />)}
                </div>
            )}
            {visibleReturns?.length === 0 && !loading && (
                <div className="text-center py-16">
                    <PackageX size={28} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-400 text-sm">
                        {nameQuery ? `No loaded returns match "${search}".` : "No returns found."}
                    </p>
                </div>
            )}

            <div className={`space-y-2 transition-opacity ${loading && returns !== null ? "opacity-50 pointer-events-none" : ""}`}>
                {visibleReturns?.map((r) => {
                    const lineCount = r.returnOrderLines?.length || 0;
                    const firstLine = r.returnOrderLines?.[0];
                    return (
                        <button key={r.returnOrderId} onClick={() => setSelectedRMA(r.returnOrderId)}
                            className="w-full bg-white rounded-xl shadow p-4 text-left hover:bg-teal-50 transition flex items-center gap-3">
                            <ProductImage src={imageBySku[firstLine?.item?.sku]} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="font-mono text-xs font-bold text-gray-500">{r.returnOrderId}</p>
                                    <span className="text-[10px] text-gray-300">· Order {r.customerOrderId}</span>
                                </div>
                                <p className="text-sm font-semibold text-gray-800 truncate">{customerFullName(r.customerName)}</p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                                    <span>{humanizeCode(r.returnType)}</span>
                                    <span>{lineCount} line{lineCount === 1 ? "" : "s"}</span>
                                    <span className="font-semibold text-gray-600">{money(r.totalRefundAmount?.currencyAmount, r.totalRefundAmount?.currencyUnit)}</span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                                    <span>Requested {fmtDate(r.returnOrderDate)}</span>
                                    {r.returnByDate && <span>Return by {fmtDate(r.returnByDate)}</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <StatusBadge status={firstLine?.status} />
                                <ChevronRight size={16} className="text-gray-300" />
                            </div>
                        </button>
                    );
                })}
            </div>

            {returns?.length > 0 && (
                <div ref={sentinelRef} className="py-6 flex items-center justify-center">
                    {loadingMore && (
                        <span className="text-xs text-gray-400 flex items-center gap-1.5">
                            <Loader size={12} className="animate-spin" /> Loading more…
                        </span>
                    )}
                    {!nextCursor && !loadingMore && (
                        <span className="text-xs text-gray-300">
                            {totalCount ? `All ${totalCount} returns loaded.` : "No more returns."}
                        </span>
                    )}
                </div>
            )}

            {selectedRMA && (
                <ReturnDetailModal returnOrderId={selectedRMA} onClose={() => setSelectedRMA(null)}
                    onRefunded={loadFirstPage} />
            )}
        </div>
    );
};

export default ReturnsPage;